import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Avatar,
  Grid,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { getComplaint } from "app/services/schoolReportingServices/complaint.service";
import { useQuery } from "react-query";
import { Star, StarBorder, ArrowBack } from "@mui/icons-material";
import { FormatFullName } from "app/utils/helper";
import { Link } from "react-router-dom";
import appRoutes from "app/config/routes";
import ComplaintConversationPreview from "./ComplaintConversationPreview";

const ComplaintDetails = () => {
  const { complaintId } = useParams();
  const navigate = useNavigate();

  // Fetch complaint details using React Query
  const {
    data: complaintResponse,
    isLoading,
    error,
  } = useQuery(
    ["complaint", complaintId],
    () => getComplaint({ complaintDocId: complaintId }),
    {
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to fetch complaint details"
        );
      },
    }
  );

  const complaint = complaintResponse?.data?.obj;

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <Typography>Loading complaint details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Failed to load complaint details</Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back to Complaints
        </Button>
      </Box>
    );
  }

  if (!complaint) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">No complaint data found</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Complaints
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mb: 4 }}>
      <Grid container spacing={2}>
        {/* Left Side (Complaint Details) */}
        <Grid item xs={12} md={6}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 2, sm: 0 }}
            mb={3}
          >
            <Typography variant="h5">
              Complaint #{complaint.complaintNumber}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
            >
              Back to Complaints
            </Button>
          </Stack>

          {/* All Complaint Details */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Complaint Details
            </Typography>

            <Stack spacing={2}>
              {/* Status + Priority in One Row */}
              <Box
                container
                spacing={2}
                sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
              >
                {/* Status */}
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: { xs: 2, sm: 4, md: 10 },
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${
                        complaint.status === "RESOLVED"
                          ? "#16a34a"
                          : complaint.status === "IN_PROGRESS"
                          ? "#ea580c"
                          : complaint.status === "CLOSED"
                          ? "#64748b"
                          : "#2563eb"
                      }`,
                      borderRadius: 5,
                      px: 2,
                      py: 0.5,
                      backgroundColor: "transparent",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          complaint.status === "RESOLVED"
                            ? "#16a34a"
                            : complaint.status === "IN_PROGRESS"
                            ? "#ea580c"
                            : complaint.status === "CLOSED"
                            ? "#64748b"
                            : "#2563eb",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      }}
                    >
                      {complaint.status?.replace("_", " ")}
                    </Typography>
                  </Box>
                </Grid>

                {/* Priority */}
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: { xs: 2, sm: 4, md: 10 },
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${
                        complaint.priority === "High"
                          ? "#dc2626"
                          : complaint.priority === "Medium"
                          ? "#ea580c"
                          : "#16a34a"
                      }`,
                      borderRadius: 5,
                      px: 2,
                      py: 0.5,
                      backgroundColor: "transparent",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          complaint.priority === "High"
                            ? "#dc2626"
                            : complaint.priority === "Medium"
                            ? "#ea580c"
                            : "#16a34a",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      }}
                    >
                      {complaint.priority}
                    </Typography>
                  </Box>
                </Grid>
              </Box>

              {/* Student Details */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Student
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  <a
                    href={`/${appRoutes.student_profile}/${complaint.studentDetail?.personDocId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#2563eb",
                      textDecoration: "underline",
                      fontWeight: 500,
                    }}
                  >
                    {FormatFullName(complaint.studentDetail?.personName)}
                  </a>
                </Typography>
                {complaint.studentDetail?.className && (
                  <Typography variant="body2" color="text.secondary">
                    Class: {complaint.studentDetail.className}
                  </Typography>
                )}
              </Box>

              {/* Complaint For */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Complaint For
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {complaint.complainFor}
                </Typography>
              </Box>

              {/* Short Description */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Short Description
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {complaint.complainShortDescription}
                </Typography>
              </Box>

              {/* Long Description */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Long Description
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {complaint.complainFullDescription}
                </Typography>
              </Box>

              {/* Created Date */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created On
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {moment(complaint.createdOn).format("DD MMM, YYYY hh:mm A")}
                </Typography>
              </Box>

              {/* Due Date */}
              {complaint.dueDate && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {moment(complaint.dueDate).format("DD MMM, YYYY")}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>

          {/* Assignment Details */}
          {complaint.assignedTo && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Assignment Details
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Assigned To
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {complaint.assignedTo.personName?.charAt(0)}
                    </Avatar>
                    <Typography variant="body1">
                      {FormatFullName(complaint.assignedTo.personName)}
                    </Typography>
                  </Box>
                </Box>

                {complaint.assigningRemark && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Assignment Remarks
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {complaint.assigningRemark}
                    </Typography>
                  </Box>
                )}

                {complaint.assignedOn && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Assigned On
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {moment(complaint.assignedOn).format(
                        "DD MMM, YYYY hh:mm A"
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Action Details */}
          {complaint.actionTakenBy && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Resolution Details
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Resolved By
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {complaint.actionTakenBy.personName?.charAt(0)}
                    </Avatar>
                    <Typography variant="body1">
                      {FormatFullName(complaint.actionTakenBy.personName)}
                    </Typography>
                  </Box>
                </Box>

                {complaint.actionDescription && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Resolution Description
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {complaint.actionDescription}
                    </Typography>
                  </Box>
                )}

                {complaint.actionTakenOn && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Resolved On
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {moment(complaint.actionTakenOn).format(
                        "DD MMM, YYYY hh:mm A"
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Feedback */}
          {complaint.feedbackRemarks && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Feedback
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Feedback Remarks
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {complaint.feedbackRemarks}
                  </Typography>
                </Box>

                {complaint.rating && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Rating
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Box key={star}>
                          {star <= complaint.rating ? (
                            <Star sx={{ color: "#ffd700", fontSize: 20 }} />
                          ) : (
                            <StarBorder sx={{ color: "#ddd", fontSize: 20 }} />
                          )}
                        </Box>
                      ))}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        ({complaint.rating}/5)
                      </Typography>
                    </Box>
                  </Box>
                )}

                {complaint.feedbackOn && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Feedback Given On
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {moment(complaint.feedbackOn).format(
                        "DD MMM, YYYY hh:mm A"
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Attachments */}
          <Typography variant="h6" mb={2}>
            Attachments
          </Typography>
          {complaint.complaintAttachment ? (
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.2s ease",
                display: "inline-block",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: 2,
                },
              }}
              onClick={() =>
                window.open(complaint.complaintAttachment, "_blank")
              }
            >
              {/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
                complaint.complaintAttachment
              ) ? (
                <img
                  src={complaint.complaintAttachment}
                  alt="Complaint Attachment"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f5f5f5",
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ fontWeight: 500 }}
                  >
                    Document
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                  >
                    Click to view
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary" mb={3}>
              No attachments
            </Typography>
          )}
        </Grid>

        {/* Right Side (Conversation Preview) - Sticky */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              height: { xs: "600px", md: "80vh" },
              display: "flex",
              flexDirection: "column",
              position: { xs: "relative", md: "sticky" },
              top: { xs: "0", md: "16px" },
              maxHeight: { xs: "600px", md: "78vh" },
            }}
          >
            <Box
              sx={{ p: 2, borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}
            >
              <Typography variant="h6">Complaint Conversation</Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
              <ComplaintConversationPreview
                complaintId={complaint._id}
                complaintNumber={complaint.complaintNumber}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplaintDetails;
